import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const RouteSegments = ({ onRouteDataChange, routeData }) => {
  const [segments, setSegments] = useState([
    {
      id: 1,
      name: 'Rotterdam to Gibraltar',
      distance: 1250,
      etsZone: 'eu-ets',
      fueleuZone: 'eu-waters',
      estimatedDuration: 3.2,
      weatherConditions: 'moderate',
      seaState: 3,
      currentSpeed: 14.5,
      isActive: true
    }
  ]);

  const [aisTrackingData, setAisTrackingData] = useState({
    currentPosition: '51°55\'N, 04°29\'E',
    currentSpeed: 14.2,
    currentCourse: 225,
    lastUpdate: '30 sec ago',
    trackingStatus: 'active'
  });

  const etsZones = [
    { value: 'eu-ets', label: 'EU ETS Waters' },
    { value: 'non-ets', label: 'Non-ETS Waters' },
    { value: 'transition', label: 'Transition Zone' }
  ];

  const fueleuZones = [
    { value: 'eu-waters', label: 'EU Waters (FuelEU Applicable)' },
    { value: 'international', label: 'International Waters' },
    { value: 'port-approach', label: 'Port Approach Zone' }
  ];

  const weatherConditions = [
    { value: 'calm', label: 'Calm (0-2 Beaufort)' },
    { value: 'moderate', label: 'Moderate (3-4 Beaufort)' },
    { value: 'rough', label: 'Rough (5-6 Beaufort)' },
    { value: 'very-rough', label: 'Very Rough (7+ Beaufort)' }
  ];

  useEffect(() => {
    if (routeData) {
      setSegments(routeData);
    }
  }, [routeData]);

  const handleSegmentChange = (segmentId, field, value) => {
    const updatedSegments = segments?.map(segment =>
      segment?.id === segmentId ? { ...segment, [field]: value } : segment
    );
    setSegments(updatedSegments);
    onRouteDataChange(updatedSegments);
  };

  const addSegment = () => {
    const newSegment = {
      id: Date.now(),
      name: `Segment ${segments?.length + 1}`,
      distance: 0,
      etsZone: 'eu-ets',
      fueleuZone: 'eu-waters',
      estimatedDuration: 0,
      weatherConditions: 'moderate',
      seaState: 3,
      currentSpeed: 14.0,
      isActive: false
    };
    const updatedSegments = [...segments, newSegment];
    setSegments(updatedSegments);
    onRouteDataChange(updatedSegments);
  };

  const removeSegment = (segmentId) => {
    if (segments?.length > 1) {
      const updatedSegments = segments?.filter(segment => segment?.id !== segmentId);
      setSegments(updatedSegments);
      onRouteDataChange(updatedSegments);
    }
  };

  const calculateSegmentEmissions = (segment) => {
    // Simplified emission calculation
    const baseEmission = (segment?.distance / 24) * 25; // Rough estimate
    const weatherMultiplier = {
      'calm': 0.9,
      'moderate': 1.0,
      'rough': 1.15,
      'very-rough': 1.3
    };
    return (baseEmission * (weatherMultiplier?.[segment?.weatherConditions] || 1.0))?.toFixed(1);
  };

  const getTotalDistance = () => {
    return segments?.reduce((total, segment) => total + (segment?.distance || 0), 0);
  };

  const getTotalDuration = () => {
    return segments?.reduce((total, segment) => total + (segment?.estimatedDuration || 0), 0);
  };

  return (
    <div className="maritime-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Route Segments</h2>
          <p className="text-sm text-muted-foreground">
            Define voyage legs with ETS and FuelEU zone classifications
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={addSegment}
          iconName="Plus"
          iconPosition="left"
        >
          Add Segment
        </Button>
      </div>
      {/* AIS Tracking Status */}
      <div className="mb-6 p-4 bg-accent/10 rounded-lg border border-accent/20">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-foreground flex items-center space-x-2">
            <Icon name="Radar" size={16} className="text-accent" />
            <span>AIS Tracking</span>
          </h3>
          <span className="text-xs text-accent">Live Position</span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-sm font-semibold text-foreground font-data">{aisTrackingData?.currentPosition}</div>
            <div className="text-xs text-muted-foreground">Current Position</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-semibold text-foreground">{aisTrackingData?.currentSpeed} kts</div>
            <div className="text-xs text-muted-foreground">Speed</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-semibold text-foreground">{aisTrackingData?.currentCourse}°</div>
            <div className="text-xs text-muted-foreground">Course</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-semibold text-foreground">{aisTrackingData?.lastUpdate}</div>
            <div className="text-xs text-muted-foreground">Last Update</div>
          </div>
        </div>
      </div>
      {/* Route Summary */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="maritime-card p-4">
          <div className="text-2xl font-bold text-foreground">{getTotalDistance()?.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">Total Distance (NM)</div>
        </div>
        <div className="maritime-card p-4">
          <div className="text-2xl font-bold text-foreground">{getTotalDuration()?.toFixed(1)}</div>
          <div className="text-sm text-muted-foreground">Total Duration (Days)</div>
        </div>
        <div className="maritime-card p-4">
          <div className="text-2xl font-bold text-foreground">{segments?.length}</div>
          <div className="text-sm text-muted-foreground">Route Segments</div>
        </div>
      </div>
      {/* Segments List */}
      <div className="space-y-4">
        {segments?.map((segment, index) => (
          <div key={segment?.id} className="border border-border rounded-lg p-4 relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  segment?.isActive 
                    ? 'bg-accent text-accent-foreground' 
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {index + 1}
                </div>
                <div>
                  <Input
                    type="text"
                    value={segment?.name}
                    onChange={(e) => handleSegmentChange(segment?.id, 'name', e?.target?.value)}
                    className="font-medium border-none p-0 h-auto bg-transparent"
                  />
                  {segment?.isActive && (
                    <span className="text-xs text-accent">Currently Active</span>
                  )}
                </div>
              </div>
              
              {segments?.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSegment(segment?.id)}
                  iconName="Trash2"
                  className="text-error hover:text-error"
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Input
                label="Distance (NM)"
                type="number"
                value={segment?.distance}
                onChange={(e) => handleSegmentChange(segment?.id, 'distance', parseFloat(e?.target?.value) || 0)}
                required
              />

              <Input
                label="Duration (Days)"
                type="number"
                step="0.1"
                value={segment?.estimatedDuration}
                onChange={(e) => handleSegmentChange(segment?.id, 'estimatedDuration', parseFloat(e?.target?.value) || 0)}
                required
              />

              <Select
                label="ETS Zone"
                options={etsZones}
                value={segment?.etsZone}
                onChange={(value) => handleSegmentChange(segment?.id, 'etsZone', value)}
                required
              />

              <Select
                label="FuelEU Zone"
                options={fueleuZones}
                value={segment?.fueleuZone}
                onChange={(value) => handleSegmentChange(segment?.id, 'fueleuZone', value)}
                required
              />

              <Select
                label="Weather Conditions"
                options={weatherConditions}
                value={segment?.weatherConditions}
                onChange={(value) => handleSegmentChange(segment?.id, 'weatherConditions', value)}
              />

              <Input
                label="Sea State"
                type="number"
                min="0"
                max="9"
                value={segment?.seaState}
                onChange={(e) => handleSegmentChange(segment?.id, 'seaState', parseInt(e?.target?.value) || 0)}
              />

              <Input
                label="Speed (knots)"
                type="number"
                step="0.1"
                value={segment?.currentSpeed}
                onChange={(e) => handleSegmentChange(segment?.id, 'currentSpeed', parseFloat(e?.target?.value) || 0)}
              />

              <div className="flex flex-col">
                <label className="text-sm font-medium text-foreground mb-1">Est. Emissions</label>
                <div className="h-10 flex items-center px-3 bg-muted rounded-md">
                  <span className="text-sm font-medium text-foreground">
                    {calculateSegmentEmissions(segment)} tCO₂e
                  </span>
                </div>
              </div>
            </div>

            {/* Zone Indicators */}
            <div className="mt-4 flex items-center space-x-4">
              <div className={`px-2 py-1 rounded text-xs font-medium ${
                segment?.etsZone === 'eu-ets' ?'bg-warning/20 text-warning' :'bg-muted text-muted-foreground'
              }`}>
                {segment?.etsZone === 'eu-ets' ? 'ETS Applicable' : 'Non-ETS'}
              </div>
              <div className={`px-2 py-1 rounded text-xs font-medium ${
                segment?.fueleuZone === 'eu-waters' ?'bg-accent/20 text-accent' :'bg-muted text-muted-foreground'
              }`}>
                {segment?.fueleuZone === 'eu-waters' ? 'FuelEU Applicable' : 'International'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RouteSegments;